import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface FeaturedToggleResult {
  success: boolean;
  error?: string;
  data?: {
    id: string;
    username: string;
    is_featured: boolean;
    updated_at: string;
  };
}

interface UseFeaturedToggleOptions {
  onSuccess?: (data: FeaturedToggleResult["data"]) => void;
  onError?: (error: string) => void;
}

export const useFeaturedToggle = (options?: UseFeaturedToggleOptions) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      profileId,
      isFeatured,
    }: {
      profileId: string;
      isFeatured: boolean;
    }) => {
      // Temporarily use direct database update instead of RPC
      const { data, error } = await supabase
        .from("profiles")
        .update({ is_featured: isFeatured })
        .eq("id", profileId)
        .select("id, username, is_featured, updated_at")
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        data: data,
      };
    },
    onMutate: ({ profileId }) => {
      // Set loading state for this specific profile
      setLoadingStates((prev) => ({ ...prev, [profileId]: true }));
    },
    onSuccess: (result, { profileId, isFeatured }) => {
      // Clear loading state
      setLoadingStates((prev) => ({ ...prev, [profileId]: false }));

      // Show success toast
      toast({
        title: "Success",
        description: `Profile ${
          isFeatured ? "featured" : "unfeatured"
        } successfully`,
      });

      // Update the cache immediately with the returned data
      if (result.data) {
        queryClient.setQueryData(["superadmin-users"], (oldData: any) => {
          if (!oldData) return oldData;

          return oldData.map((user: any) =>
            user.id === profileId
              ? { ...user, is_featured: result.data!.is_featured }
              : user
          );
        });

        // Also update any other relevant caches
        queryClient.setQueryData(["profiles"], (oldData: any) => {
          if (!oldData) return oldData;

          return oldData.map((profile: any) =>
            profile.id === profileId
              ? { ...profile, is_featured: result.data!.is_featured }
              : profile
          );
        });
      }

      // Call success callback if provided
      if (options?.onSuccess && result.data) {
        options.onSuccess(result.data);
      }
    },
    onError: (error: Error, { profileId }) => {
      // Clear loading state
      setLoadingStates((prev) => ({ ...prev, [profileId]: false }));

      // Show error toast
      toast({
        title: "Error",
        description: error.message || "Failed to update featured status",
        variant: "destructive",
      });

      // Call error callback if provided
      if (options?.onError) {
        options.onError(error.message);
      }
    },
  });

  // Use useCallback to prevent multiple rapid calls
  const toggleFeatured = useCallback(
    (profileId: string, currentFeaturedStatus: boolean) => {
      // Prevent multiple rapid calls
      if (loadingStates[profileId]) {
        return;
      }

      mutation.mutate({
        profileId,
        isFeatured: !currentFeaturedStatus,
      });
    },
    [mutation, loadingStates]
  );

  const isLoading = useCallback(
    (profileId: string) => {
      return loadingStates[profileId] || false;
    },
    [loadingStates]
  );

  return {
    toggleFeatured,
    isLoading,
    isGeneralLoading: mutation.isPending,
    error: mutation.error?.message || null,
  };
};
